import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/** Matches the API NotificationChannel enum. Change labels here if different. */
export enum NotificationChannel {
  Email = 1,
  Sms = 2,
  WhatsApp = 3,
}

export const NOTIFICATION_CHANNELS = [
  { value: NotificationChannel.Email, label: 'Email', icon: 'pi pi-envelope' },
  { value: NotificationChannel.Sms, label: 'SMS', icon: 'pi pi-comment' },
  { value: NotificationChannel.WhatsApp, label: 'WhatsApp', icon: 'pi pi-whatsapp' },
];

export function channelLabel(value: number): string {
  return NOTIFICATION_CHANNELS.find((c) => c.value === value)?.label ?? `#${value}`;
}

/** POST /api/Notification/Send -> SendNotificationRequest */
export interface SendNotificationRequest {
  channel: NotificationChannel;
  recipient: string;
  subject?: string | null;
  body: string;
  scheduledAt?: string | null;
}

/**
 * POST /api/Notification/SendTemplate (Admin-only) — queue a message rendered from a
 * MessageTemplate, optionally carrying a file uploaded through /api/File/Upload.
 *
 * Pass the upload's `key` as mediaStorageKey — NOT its presigned url: the WhatsApp sender
 * re-presigns from the key at send time, so a delivery retry hours later still works.
 * Empty `channels` = the server resolves them from `messageType` via MessageRule.
 */
export interface SendTemplateMessageRequest {
  templateKey: string;
  /** Channel NAMES ('WHATSAPP' | 'SMS' | 'EMAIL'); empty = resolve from messageType. */
  channels?: string[];
  mobile?: string | null;
  email?: string | null;
  /** Scope the template lookup (and channel rules) to a partner or a client. */
  partnerId?: number | null;
  clientId?: number | null;
  /** {{Key}} -> value substitutions; {{Company}} is added automatically by the server. */
  placeholders?: Record<string, string>;
  /** Fallback wording used only when no template row matches. */
  subject?: string | null;
  body?: string | null;
  /** Classifies the outbox row; resolves the channels when `channels` is empty. */
  messageType?: string | null;
  mediaStorageKey?: string | null;
  mediaUrl?: string | null;
  mediaFileName?: string | null;
  mediaMimeType?: string | null;
  scheduledAt?: string | null;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/Notification`;

  send(body: SendNotificationRequest) {
    return this.http.post(`${this.baseUrl}/Send`, body);
  }

  /** Queue a template-rendered message (optionally with an attachment). Admin-only.
   *  Success `data` is `{ channels: string[], attached: boolean }` — what was QUEUED,
   *  not delivered; delivery shows up in the outbox (GetAll). */
  sendTemplate(body: SendTemplateMessageRequest) {
    return this.http.post(`${this.baseUrl}/SendTemplate`, body);
  }

  getAll() {
    return this.http.get(`${this.baseUrl}/GetAll`);
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } });
  }

  retry(id: number) {
    return this.http.post(`${this.baseUrl}/Retry`, {}, { params: { id } });
  }
}
